<template>
  <form @submit.prevent="$emit('submit')">
    <div class="space-y-6 pb-12">
      <!-- Language switcher (controls all localized sections) -->
      <div
        v-if="showLocaleSwitcher"
        class="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-6 py-4"
      >
        <div>
          <h2 class="text-base font-semibold text-gray-900">
            Wersja językowa
          </h2>
          <p class="mt-1 text-sm text-gray-500">
            Przełącz, aby edytować treści w wybranym języku. Pola poza sekcjami treści są wspólne dla wszystkich języków.
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            :class="localeTabClass('pl')"
            @click="activeEditingLocale = 'pl'"
          >
            PL
          </button>
          <button
            type="button"
            :class="localeTabClass('en')"
            @click="activeEditingLocale = 'en'"
          >
            EN
          </button>
        </div>
      </div>

      <!-- Dane z MedfileAPI -->
      <div>
        <h2 class="mb-3 px-1 text-sm font-semibold text-gray-900">
          Dane z MedfileAPI
        </h2>
        <div class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-950/5">
          <section class="px-6 py-8 sm:px-8">
            <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div class="medfile-synced-fields opacity-60">
                <text-input
                  :model-value="loc.remote_name || ''"
                  class="w-full"
                  :label="`Nazwa (${activeEditingLocale.toUpperCase()})`"
                  readonly
                  tabindex="-1"
                />
              </div>

              <div
                v-if="availableSpecialists && availableSpecialists.length > 0"
                :class="syncedFieldsShellClass"
              >
                <label class="form-label" :for="specialistsSelectId">Specjaliści</label>
                <div ref="specialistsWrapper" class="admin-multiselect-wrapper mt-1">
                  <Multiselect
                    :id="specialistsSelectId"
                    v-model="form.service.specialist_ids"
                    class="admin-multiselect"
                    mode="tags"
                    :options="specialistOptions"
                    value-prop="value"
                    label="label"
                    :searchable="!lockSyncedFields"
                    :filter-results="true"
                    :close-on-select="false"
                    :hide-selected="false"
                    :disabled="lockSyncedFields"
                    placeholder="Wybierz specjalistów…"
                    no-options-text="Brak specjalistów"
                    no-results-text="Brak wyników"
                    @open="positionDropdown"
                  />
                </div>
                <div v-if="specialistErrors.length" class="form-error">
                  {{ specialistErrors[0] }}
                </div>
              </div>

              <toggle-switch
                v-if="showActiveToggle"
                v-model="form.service.active"
                :errors="form.errors['service.active'] || []"
                class="w-full"
                label="Aktywna"
                description="Widoczna na stronie publicznej"
              />
            </div>
          </section>
        </div>
      </div>

      <!-- Dane lokalne -->
      <div>
        <h2 class="mb-3 px-1 text-sm font-semibold text-gray-900">
          Dane lokalne
        </h2>
        <div class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-950/5">
          <div class="divide-y divide-gray-100">
            <!-- Nazwa lokalna -->
            <section class="px-6 py-8 sm:px-8">
              <div class="mb-5 flex flex-wrap items-center justify-between gap-4">
                <h2 class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Nazwa lokalna ({{ activeEditingLocale.toUpperCase() }})
                </h2>
                <toggle-switch
                  v-model="form.service.use_local_name"
                  :errors="form.errors['service.use_local_name'] || []"
                  description="Używaj nazwy lokalnej"
                />
              </div>
              <text-input
                v-model="loc.name"
                :errors="localeErrors('name')"
                class="w-full"
                :required="activeEditingLocale === 'pl'"
                maxlength="255"
              />
              <p class="mt-2 text-xs text-gray-400">
                Gdy przełącznik jest włączony, wszędzie wyświetlana jest ta nazwa (PL/EN).
                W przeciwnym razie pokazujemy nazwę z MedfileAPI.
              </p>
            </section>

            <!-- Opis -->
            <section class="px-6 py-8 sm:px-8">
              <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Opis ({{ activeEditingLocale.toUpperCase() }})
              </h2>
              <textarea-input
                v-model="loc.description"
                :errors="localeErrors('description')"
                class="w-full"
                rows="6"
                maxlength="5000"
              />
            </section>

            <!-- Kategoria -->
            <section class="px-6 py-8 sm:px-8">
              <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Kategoria
              </h2>
              <service-category-select
                v-model="form.service.category"
                :errors="form.errors['service.category'] || []"
                :options="categoryOptionsList"
                allow-blank
                highlight-when-blank
                blank-label="Wybierz kategorię"
                compact
                class="w-full sm:max-w-xs"
              />
            </section>

            <!-- Ważne informacje -->
            <section class="px-6 py-8 sm:px-8">
              <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Ważne informacje ({{ activeEditingLocale.toUpperCase() }})
              </h2>
              <div class="space-y-3">
                <div
                  v-for="(item, index) in loc.important_information"
                  :key="index"
                  class="flex items-start gap-2"
                >
                  <textarea
                    v-model="loc.important_information[index]"
                    rows="2"
                    class="form-input min-h-[72px] flex-1 rounded-lg"
                    placeholder="Wpisz informację…"
                  />
                  <button
                    type="button"
                    class="inline-flex shrink-0 items-center justify-center rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                    title="Usuń"
                    @click="removeImportantInfo(index)"
                  >
                    <icon name="trash" class="size-5 stroke-current" />
                  </button>
                </div>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 transition hover:text-indigo-700"
                  @click="addImportantInfo"
                >
                  <span aria-hidden="true" class="text-base leading-none">+</span>
                  Dodaj informację
                </button>
              </div>
            </section>

            <!-- FAQ -->
            <section class="px-6 py-8 sm:px-8">
              <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                FAQ ({{ activeEditingLocale.toUpperCase() }})
              </h2>
              <div class="space-y-5">
                <div
                  v-for="(item, index) in loc.faq"
                  :key="index"
                  class="rounded-xl bg-gray-50/80 p-4 sm:p-5"
                >
                  <div class="mb-4 flex items-center justify-between gap-2">
                    <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Pytanie {{ index + 1 }}
                    </span>
                    <button
                      type="button"
                      class="inline-flex shrink-0 items-center justify-center rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                      title="Usuń"
                      @click="removeFaqItem(index)"
                    >
                      <icon name="trash" class="size-5 stroke-current" />
                    </button>
                  </div>
                  <text-input
                    v-model="item.question"
                    class="w-full pb-4"
                    label="Pytanie"
                    maxlength="500"
                  />
                  <textarea-input
                    v-model="item.answer"
                    class="w-full"
                    label="Odpowiedź"
                    rows="3"
                    maxlength="5000"
                  />
                </div>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 transition hover:text-indigo-700"
                  @click="addFaqItem"
                >
                  <span aria-hidden="true" class="text-base leading-none">+</span>
                  Dodaj pytanie
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
    <slot />
  </form>
</template>

<script>
import TextInput from '@/Shared/TextInput.vue';
import TextareaInput from '@/Shared/TextareaInput.vue';
import ServiceCategorySelect from '@/Shared/ServiceCategorySelect.vue';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import Icon from '@/Shared/Icon.vue';
import Multiselect from '@vueform/multiselect';
import { SERVICE_CATEGORIES } from '@/utils/serviceCategoryLabels';

export default {
  components: {
    TextInput,
    TextareaInput,
    ServiceCategorySelect,
    ToggleSwitch,
    Icon,
    Multiselect,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    availableSpecialists: {
      type: Array,
      default: () => [],
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    editingLocale: {
      type: String,
      default: undefined,
    },
    showLocaleSwitcher: {
      type: Boolean,
      default: true,
    },
    showActiveToggle: {
      type: Boolean,
      default: true,
    },
    lockSyncedFields: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:editingLocale', 'submit'],
  data() {
    return {
      specialistsSelectId: `service-specialists-${Math.random().toString(36).slice(2, 9)}`,
      internalEditingLocale: 'pl',
    };
  },
  computed: {
    form: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    activeEditingLocale: {
      get() {
        return this.editingLocale !== undefined ? this.editingLocale : this.internalEditingLocale;
      },
      set(val) {
        if (this.editingLocale !== undefined) {
          this.$emit('update:editingLocale', val);
        } else {
          this.internalEditingLocale = val;
        }
      },
    },
    loc() {
      const slice = this.form.service[`data_${this.activeEditingLocale}`];
      if (!Array.isArray(slice.important_information)) {
        slice.important_information = [];
      }
      if (!Array.isArray(slice.faq)) {
        slice.faq = [];
      }
      return slice;
    },
    categoryOptionsList() {
      return this.categoryOptions.length > 0 ? this.categoryOptions : SERVICE_CATEGORIES;
    },
    specialistErrors() {
      const errors = this.form.errors || {};
      return (
        errors['service.specialist_ids'] ||
        errors.specialist_ids ||
        []
      );
    },
    specialistOptions() {
      return (this.availableSpecialists || []).map((specialist) => ({
        value: specialist.id,
        label: specialist.label || specialist.full_name,
      }));
    },
    syncedFieldsShellClass() {
      if (!this.lockSyncedFields) {
        return null;
      }

      return 'medfile-synced-fields opacity-60';
    },
  },
  created() {
    if (typeof this.form.service.use_local_name !== 'boolean') {
      this.form.service.use_local_name = Boolean(this.form.service.use_local_name);
    }
    if (!Array.isArray(this.form.service.specialist_ids)) {
      this.form.service.specialist_ids = [];
    }
    ['data_pl', 'data_en'].forEach((key) => {
      if (!this.form.service[key]) {
        this.form.service[key] = { name: '', remote_name: '', description: '' };
      }
      if (typeof this.form.service[key].remote_name !== 'string') {
        this.form.service[key].remote_name = this.form.service[key].remote_name || '';
      }
      if (!Array.isArray(this.form.service[key].important_information)) {
        this.form.service[key].important_information = [];
      }
      if (!Array.isArray(this.form.service[key].faq)) {
        this.form.service[key].faq = [];
      }
    });
  },
  methods: {
    localeTabClass(localeCode) {
      return [
        'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
        this.activeEditingLocale === localeCode
          ? 'bg-indigo-500 text-white'
          : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
      ];
    },
    localeErrors(field) {
      const errors = this.form.errors || {};
      const raw =
        errors[`service.data_${this.activeEditingLocale}.${field}`] ||
        errors[`service.${field}`] ||
        errors[field];
      if (!raw) return [];
      return Array.isArray(raw) ? raw : [raw];
    },
    positionDropdown() {
      this.$nextTick(() => {
        const wrapper = this.$refs.specialistsWrapper;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        wrapper.style.setProperty('--ms-fixed-top', `${rect.bottom + 2}px`);
        wrapper.style.setProperty('--ms-fixed-left', `${rect.left}px`);
        wrapper.style.setProperty('--ms-fixed-width', `${rect.width}px`);
      });
    },
    addImportantInfo() {
      this.loc.important_information.push('');
    },
    removeImportantInfo(index) {
      this.loc.important_information.splice(index, 1);
    },
    addFaqItem() {
      this.loc.faq.push({ question: '', answer: '' });
    },
    removeFaqItem(index) {
      this.loc.faq.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.medfile-synced-fields :deep(.form-label) {
  color: var(--color-green-700);
}

.medfile-synced-fields :deep(.form-input),
.medfile-synced-fields :deep(.form-textarea) {
  cursor: default;
  border-color: var(--color-green-200);
  background-color: var(--color-green-50);
  color: var(--color-green-800);
}

.medfile-synced-fields :deep(.form-input:focus),
.medfile-synced-fields :deep(.form-textarea:focus) {
  border-color: var(--color-green-300);
  outline: none;
  box-shadow: none;
}

.medfile-synced-fields :deep(.admin-multiselect) {
  cursor: default;
  border-color: var(--color-green-200);
  background-color: var(--color-green-50);
}

.medfile-synced-fields :deep(.admin-multiselect.is-disabled) {
  opacity: 1;
}
</style>
